import "./App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import React, { Suspense, lazy, useState, createContext } from "react";
import AuthProvider from "./Context/AuthProvider";
import { Box, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import loading from "./Components/Vendors/Images/loading.gif";
import Login from "./Components/Authentication/Login/Login";
import { useAuthStore } from "./Hooks/store/useAuthStore.js";
import useSound from "use-sound";
import msgSend from "./../src/images/Sms Tone.mp3";
import theme from "./Hooks/store/theme";
import { fadeIn, tokens } from "./Hooks/store/tokens.js";
import { isAccountVerified } from "./Components/Authentication/SharedLogItem/Verification.jsx";
import { SessionGuard } from "./Hooks/store/useSessionUser.jsx";

const Routing = lazy(() => import("./Components/Routing/Routing.js"));
const Signup = lazy(() => import("./Components/Authentication/SignUp/Signup"));
const ForgetPassword = lazy(() =>
  import("./Components/Authentication/ForgetPass/ForgetPassword")
);

const VerifyAccount = lazy(() =>
  import("./Components/Authentication/SharedLogItem/VerifyAccount.jsx")
);

export const UserContext = createContext();
export const userTypeContext = createContext();

const VERIFY_PATH = "/verify-account";
const AUTH_PATHS = ["/login", "/signup", "/register", "/forgetpass", VERIFY_PATH];

// Only follow a "from" that is a real destination, never an auth page.
const safeTarget = (from) =>
  from && !AUTH_PATHS.includes(from.split("?")[0].toLowerCase()) ? from : "/";

// Logged out -> /login, remembering where they were going.
const RedirectToLogin = () => {
  const location = useLocation();
  return (
    <Navigate
      to="/login"
      replace
      state={{ from: location.pathname + location.search }}
    />
  );
};

// Signed in: unverified accounts (mail_verify / phone_verify not 1) can only
// see the verify page; verified accounts get the full app.
const SignedInRoutes = ({ verified }) => {
  const location = useLocation();
  const onVerifyPage = location.pathname.toLowerCase() === VERIFY_PATH;

  if (!verified) {
    if (onVerifyPage) return <VerifyAccount />;
    return (
      <Navigate
        to={VERIFY_PATH}
        replace
        state={{ from: location.state?.from ?? location.pathname + location.search }}
      />
    );
  }

  // Just verified (or opened the page by hand): continue to where they were headed.
  if (onVerifyPage) {
    return <Navigate to={safeTarget(location.state?.from)} replace />;
  }
  return <Routing />;
};

function App() {
  const [userData, setUserData] = useState({});
  const [userTypeInfo, setUserTypeInfo] = useState({});

  const {
    connectSocket,
    setNotifSeen,
    socket,
    setInComingMessageNoti,
    msgUserList,
    inComingMessageNoti,
    setMsgUserList,
    fetchMsgUserList,
    user,
    userPage,
  } = useAuthStore();
  const [play] = useSound(msgSend);

  // Single source of truth: the zustand store (hydrated from "gh_sl@g").
  const isLoggedIn = !!user?.sopnoid;
  const isVerified = isAccountVerified(user);
  const isReady = isLoggedIn && isVerified; // signed in AND verified

  // Realtime + data only for verified sessions.
  React.useEffect(() => {
    if (!isReady) return;
    connectSocket(); // no-op if already connected/connecting
  }, [isReady, connectSocket]);

  React.useEffect(() => {
    if (!isReady) return;
    fetchMsgUserList();
  }, [isReady, fetchMsgUserList, userPage]);

  function handleUnreadMsg() {
    setNotifSeen(false);
    const updatedUserList = msgUserList?.map((u) => {
      if (u?.m_room === inComingMessageNoti?.subj_id) {
        return {
          ...u,
          unread: (u.unread || 0) + (inComingMessageNoti?.total || 0),
        };
      }
      return u;
    });
    setMsgUserList(updatedUserList);
    play();
  }

  React.useEffect(() => {
    if (inComingMessageNoti && inComingMessageNoti?.sender !== user?.sopnoid) {
      handleUnreadMsg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inComingMessageNoti]);

  React.useEffect(() => {
    if (!socket || !user?.sopnoid) return;

    socket.emit("getUserNotifications", user.sopnoid);
    socket.on("receiveNotification", (notifiR) => {
      setInComingMessageNoti({ ...notifiR });
    });

    return () => {
      socket.off("receiveNotification");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user?.sopnoid]);

  return (
    <div>
      <UserContext.Provider value={[userData, setUserData]}>
        <userTypeContext.Provider value={[userTypeInfo, setUserTypeInfo]}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
              <Suspense
                fallback={
                  <Box
                    sx={{
                      height: "100vh",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      bgcolor: tokens.bg.page,
                      gap: 2,
                      ...fadeIn,
                      animation: "fadeIn .3s ease both",
                    }}
                  >
                    <Box
                      component="img"
                      src={loading}
                      alt="Loading"
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        boxShadow: tokens.shadow.sm,
                      }}
                    />
                  </Box>
                }
              >
                <BrowserRouter>
                  {/* Restores the session, redirects to /login when there is
                      none, and reacts to logout in other tabs. */}
                  <SessionGuard />

                  {/* Logged out: only the auth pages exist. */}
                  {!isLoggedIn && (
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/forgetpass" element={<ForgetPassword />} />
                      {/* /register was the old signup form; the OTP-verified
                          wizard at /signup replaces it. */}
                      <Route path="/register" element={<Navigate to="/signup" replace />} />
                      <Route path="*" element={<RedirectToLogin />} />
                    </Routes>
                  )}

                  {/* Signed in: verify first, then the full app. */}
                  {isLoggedIn && <SignedInRoutes verified={isVerified} />}
                </BrowserRouter>
              </Suspense>
            </AuthProvider>
          </ThemeProvider>
        </userTypeContext.Provider>
      </UserContext.Provider>
    </div>
  );
}

export default App;